<template>
  <b-card>
    <div style="position: relative; width: 100%">
      <canvas
        ref="chartCanvas"
        :width="width"
        :height="height"
        @mousemove="onMouseMove"
        @mouseleave="onMouseLeave"
      ></canvas>
      <div
        v-if="tooltip.show"
        class="day-tooltip"
        :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
      >
        <div class="day-tooltip-title">Day {{ tooltip.day }}</div>
        <div>{{ tooltip.dateRange }}</div>
        <div v-if="tooltip.auc !== null">
          AUC₂₄: {{ tooltip.auc }} mg·hr/L
          <em>{{
            tooltip.isSimulated
              ? tooltip.isPartial
                ? '(predicted, scaled to 24h)'
                : '(predicted)'
              : '(estimated)'
          }}</em>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, reactive } from 'vue';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-moment';
import { markRaw } from 'vue';
import { DoseTypes } from '@/models/utils/constants';
import moment from 'moment/moment';
import { CalculationType } from '@/models/Enums';
import { useDosingStore } from '@/stores/dosing';

Chart.register(...registerables);

defineOptions({ name: 'DosePlanGraph' });

const props = defineProps<{ dosePlan: any; showSecondary?: boolean }>();

const store = useDosingStore();
const chartCanvas = ref<HTMLCanvasElement | null>(null);
const width = 300;
const height = 250;
const chart = ref<Chart | null>(null);

const tooltip = reactive({
  show: false,
  x: 0,
  y: 0,
  day: 0,
  dateRange: '',
  auc: null as string | null,
  isSimulated: false,
  isPartial: false,
});

const simulation = computed(() => store.getSimulation);
const dailyAucs = computed(() => store.getSimulation?.dailyAucs ?? []);

interface DayBound {
  day: number;
  startTime: Date;
  endTime: Date; // always a full 24h boundary; may extend past simulation data
  auc: number | null; // scaled to 24h when partial
  isSimulated: boolean;
  isPartial: boolean; // true when raw data covered less than a full 24h window
}

/** Linear interpolation into a concentration-time series at targetMs. */
function interpolateConcSeries(
  series: Array<{ time: any; concentration: number }>,
  targetMs: number,
): number {
  for (let i = 0; i < series.length - 1; i++) {
    const t1 = new Date(series[i].time).getTime();
    const t2 = new Date(series[i + 1].time).getTime();
    if (t1 <= targetMs && targetMs <= t2) {
      const frac = (targetMs - t1) / (t2 - t1);
      return (
        series[i].concentration + frac * (series[i + 1].concentration - series[i].concentration)
      );
    }
  }
  if (series.length === 0) return 0;
  if (targetMs <= new Date(series[0].time).getTime()) return series[0].concentration;
  return series[series.length - 1].concentration;
}

/** Trapezoidal AUC over a concentration-time series within [windowStartMs, windowEndMs]. */
function trapezoidalAucSeries(
  series: Array<{ time: any; concentration: number }>,
  windowStartMs: number,
  windowEndMs: number,
): number {
  if (!series.length) return 0;
  const pts: Array<{ t: number; c: number }> = [
    { t: windowStartMs, c: interpolateConcSeries(series, windowStartMs) },
  ];
  for (const p of series) {
    const t = new Date(p.time).getTime();
    if (t > windowStartMs && t < windowEndMs) pts.push({ t, c: p.concentration });
  }
  pts.push({ t: windowEndMs, c: interpolateConcSeries(series, windowEndMs) });
  pts.sort((a, b) => a.t - b.t);

  let auc = 0;
  for (let i = 1; i < pts.length; i++) {
    const dt = (pts[i].t - pts[i - 1].t) / 3_600_000; // ms → hours
    auc += ((pts[i - 1].c + pts[i].c) / 2) * dt;
  }
  return auc;
}

/**
 * Build the full set of day windows spanning both the administered history
 * and the simulated dose plan section.
 *
 * When a dose plan is selected we ignore the pre-computed `dailyAucs` windows
 * and instead build the full timeline from scratch using the plan's own
 * administeredTimeSeries + mainTimeSeries. This ensures Day 1 always starts
 * at the first administered dose, even when the administered history is
 * shorter than 24 hours.
 *
 * When no plan is selected we fall back to the dailyAucs windows (administered
 * history only).
 */
function computeDayBounds(aucs: typeof dailyAucs.value, dosePlan: any): DayBound[] {
  const msPerDay = 24 * 60 * 60 * 1000;

  // ── No plan selected: show administered windows only ─────────────────────
  if (!dosePlan?.mainTimeSeries?.length) {
    return aucs.map((d) => ({ ...d, isSimulated: false, isPartial: false }));
  }

  // ── Plan selected: build unified windows from first admin dose ─────────────
  const mainSeries: Array<{ time: any; concentration: number }> = dosePlan.mainTimeSeries;
  const adminSeries: Array<{ time: any; concentration: number }> =
    dosePlan.administeredTimeSeries ?? [];

  // Merge both series into one sorted timeline for AUC computation
  const combined = [...adminSeries, ...mainSeries].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
  );

  // Day 1 starts at the first administered dose (or plan start if no history)
  const firstTimeMs =
    adminSeries.length > 0
      ? new Date(adminSeries[0].time).getTime()
      : new Date(mainSeries[0].time).getTime();

  // Boundary between historical and simulated sections
  const adminEndMs =
    adminSeries.length > 0
      ? new Date(adminSeries[adminSeries.length - 1].time).getTime()
      : firstTimeMs;

  const lastTimeMs = new Date(mainSeries[mainSeries.length - 1].time).getTime();

  const result: DayBound[] = [];
  let dayStart = firstTimeMs;
  let dayNumber = 1;

  while (dayStart < lastTimeMs) {
    // endTime is always a full 24h boundary so shading is consistent
    const fullDayEnd = dayStart + msPerDay;
    // dataEnd is the actual last data point — may be less than a full day
    const dataEnd = Math.min(fullDayEnd, lastTimeMs);
    const dataHours = (dataEnd - dayStart) / 3_600_000;
    const isPartial = dataEnd < fullDayEnd;

    const rawAuc = trapezoidalAucSeries(combined, dayStart, dataEnd);
    // Scale partial windows to a 24-hour equivalent
    const auc = isPartial ? rawAuc * (24 / dataHours) : rawAuc;

    // A window is "simulated" only when it lies entirely within the future section
    const isSimulated = dayStart >= adminEndMs;
    result.push({
      day: dayNumber,
      startTime: new Date(dayStart),
      endTime: new Date(fullDayEnd),
      auc,
      isSimulated,
      isPartial,
    });
    dayStart += msPerDay;
    dayNumber++;
  }

  return result;
}

// Custom plugin: draw day-region backgrounds + boundary lines
const dayBoundaryPlugin = markRaw({
  id: 'dayBoundaries',

  // Draw alternating background fills before the datasets are painted
  beforeDraw(chartInstance: Chart) {
    const bounds = computeDayBounds(dailyAucs.value, props.dosePlan);
    if (!bounds.length) return;

    const ctx = chartInstance.ctx;
    const xScale = chartInstance.scales['x'];
    const yScale = chartInstance.scales['y'];
    if (!xScale || !yScale) return;

    ctx.save();
    for (let i = 0; i < bounds.length; i++) {
      if (i % 2 !== 0) continue; // shade odd-indexed slots (day 1, 3, 5 …)
      const b = bounds[i];
      const x1 = xScale.getPixelForValue(b.startTime.getTime());
      const x2 = xScale.getPixelForValue(b.endTime.getTime());
      const cx1 = Math.max(x1, xScale.left);
      const cx2 = Math.min(x2, xScale.right);
      if (cx2 <= cx1) continue;
      ctx.fillStyle = b.isSimulated ? 'rgba(90, 130, 230, 0.07)' : 'rgba(120, 120, 120, 0.09)';
      ctx.fillRect(cx1, yScale.top, cx2 - cx1, yScale.bottom - yScale.top);
    }
    ctx.restore();
  },

  // Draw boundary lines (between days, not at the very start) and day labels
  afterDraw(chartInstance: Chart) {
    const bounds = computeDayBounds(dailyAucs.value, props.dosePlan);
    if (!bounds.length) return;

    const ctx = chartInstance.ctx;
    const xScale = chartInstance.scales['x'];
    const yScale = chartInstance.scales['y'];
    if (!xScale || !yScale) return;

    ctx.save();
    ctx.font = '10px sans-serif';

    for (let i = 0; i < bounds.length; i++) {
      const b = bounds[i];

      // Boundary line: skip the very first window's start (that's the chart origin)
      if (i > 0) {
        const x = xScale.getPixelForValue(b.startTime.getTime());
        if (x >= xScale.left && x <= xScale.right) {
          ctx.strokeStyle = b.isSimulated ? 'rgba(80, 100, 200, 0.30)' : 'rgba(80, 80, 80, 0.35)';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(x, yScale.top);
          ctx.lineTo(x, yScale.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      // Day label at the start of each window
      const xLabel = xScale.getPixelForValue(b.startTime.getTime());
      const cxLabel = Math.max(xLabel, xScale.left);
      if (cxLabel < xScale.right) {
        ctx.fillStyle = b.isSimulated ? 'rgba(60, 80, 180, 0.55)' : 'rgba(80, 80, 80, 0.60)';
        ctx.fillText(`Day ${b.day}`, cxLabel + 3, yScale.top + 11);
      }
    }
    ctx.restore();
  },
});

function onMouseMove(event: MouseEvent) {
  const c = chart.value;
  const canvas = chartCanvas.value;
  const bounds = computeDayBounds(dailyAucs.value, props.dosePlan);
  if (!c || !canvas || !bounds.length) {
    tooltip.show = false;
    return;
  }

  const rect = canvas.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const xScale = c.scales['x'];
  if (!xScale) return;

  const mouseTime = xScale.getValueForPixel(mouseX);
  if (mouseTime == null) {
    tooltip.show = false;
    return;
  }

  for (const b of bounds) {
    if (mouseTime >= b.startTime.getTime() && mouseTime < b.endTime.getTime()) {
      const startLabel = moment(b.startTime).format('DD MMM HH:mm');
      const endLabel = moment(b.endTime).format('DD MMM HH:mm');
      tooltip.show = true;
      tooltip.x = mouseX + 8;
      tooltip.y = 20;
      tooltip.day = b.day;
      tooltip.dateRange = `${startLabel} – ${endLabel}`;
      tooltip.auc = b.auc !== null ? b.auc.toFixed(1) : null;
      tooltip.isSimulated = b.isSimulated;
      tooltip.isPartial = b.isPartial;
      return;
    }
  }
  tooltip.show = false;
}

function onMouseLeave() {
  tooltip.show = false;
}

const chartOptions = markRaw({
  responsive: true,
  scales: {
    x: { type: 'time' as const, time: { unit: 'day' as const }, position: 'bottom' as const },
    y: { title: { display: true, text: 'mg/L' } },
  },
  plugins: {
    legend: { labels: { filter: (item: any) => item.text !== 'DND' } },
  },
});

const getMeasuredConcentrations = computed(() => {
  const sim = store.getSimulation;
  if (!sim) return [];
  return DoseTypes.CONC in sim._dosesByType
    ? sim._dosesByType[DoseTypes.CONC].map((d: any) => ({ x: d.datetime, y: d.amount }))
    : [];
});

const getPeakLevel = computed(() => {
  const peak = props.dosePlan?.mainResult?.peak;
  return props.dosePlan?.mainTimeSeries?.map((d: any) => ({ x: d.time, y: peak })) ?? [];
});

const getTroughLevel = computed(() => {
  const trough = props.dosePlan?.mainResult?.trough;
  return props.dosePlan?.mainTimeSeries?.map((d: any) => ({ x: d.time, y: trough })) ?? [];
});

const getSteadyStateLevel = computed(() => {
  const ss = props.dosePlan?.mainResult?.ss;
  return props.dosePlan?.mainTimeSeries?.map((d: any) => ({ x: d.time, y: ss })) ?? [];
});

const getChartData = computed(() => {
  const chartDataToReturn: { datasets: any[] } = { datasets: [] };
  const sim = simulation.value;
  if (!sim) return chartDataToReturn;

  if (sim.hasMeasured()) {
    chartDataToReturn.datasets.push({
      label: 'Measured',
      data: getMeasuredConcentrations.value,
      showLine: false,
      pointRadius: 7,
      pointStyle: 'rectRot',
      borderWidth: 1,
      backgroundColor: 'rgba(168, 50, 133, 0.8)',
      borderColor: 'rgba(168, 30, 103, 0.9)',
    });
  }

  if (props.dosePlan == null) {
    const popAdministered = sim
      .adminDoseConcForType(CalculationType.POPULATION)
      .map((d: any) => ({ x: d.time, y: d.concentration }));
    const patAdministered = sim
      .adminDoseConcForType(CalculationType.PATIENT)
      .map((d: any) => ({ x: d.time, y: d.concentration }));

    if (patAdministered.length) {
      chartDataToReturn.datasets.push({
        label: CalculationType.PATIENT,
        data: patAdministered,
        showLine: true,
        backgroundColor: 'rgba(0, 0, 32, 0.8)',
        borderColor: 'rgba(0, 0, 0, 0.9)',
      });
      if (props.showSecondary) {
        chartDataToReturn.datasets.push({
          label: CalculationType.POPULATION,
          data: popAdministered,
          showLine: true,
          hidden: true,
          borderWidth: 1,
          borderDash: [10, 5],
          backgroundColor: 'rgba(17, 150, 255, 0.5)',
          borderColor: 'rgba(0, 120, 255, 0.6)',
        });
      }
    } else {
      chartDataToReturn.datasets.push({
        label: CalculationType.POPULATION,
        data: popAdministered,
        showLine: true,
        backgroundColor: 'rgba(17, 150, 255, 0.8)',
        borderColor: 'rgba(0, 120, 255, 0.9)',
      });
    }
    const days = sim._delivery?.daysToDisplay ?? 3;
    const endDate = moment(Date.now()).add(days, 'days').toDate();
    chartDataToReturn.datasets.push({
      label: 'DND',
      data: [{ x: endDate, y: 0 }],
      showLine: false,
      pointRadius: 0,
    });
    return chartDataToReturn;
  }

  props.dosePlan.calculateTimeSeries(sim);
  const interval = 'simulated';

  if (props.dosePlan.administeredTimeSeries != null) {
    chartDataToReturn.datasets.push({
      label: 'Given',
      data: props.dosePlan.administeredTimeSeries.map((d: any) => ({
        x: d.time,
        y: d.concentration,
      })),
      showLine: true,
      backgroundColor: props.dosePlan.secondaryAdministeredTimeSeries
        ? 'rgba(0, 0, 32, 0.8)'
        : 'rgba(17, 150, 255, 0.8)',
      borderColor: props.dosePlan.secondaryAdministeredTimeSeries
        ? 'rgba(0, 0, 0, 0.9)'
        : 'rgba(0, 120, 255, 0.9)',
    });
  }
  if (props.showSecondary && props.dosePlan.secondaryAdministeredTimeSeries) {
    chartDataToReturn.datasets.push({
      label: 'Given(population)',
      data: props.dosePlan.secondaryAdministeredTimeSeries.map((d: any) => ({
        x: d.time,
        y: d.concentration,
      })),
      showLine: true,
      hidden: true,
      borderWidth: 1,
      borderDash: [10, 5],
      backgroundColor: 'rgba(17, 150, 255, 0.8)',
      borderColor: 'rgba(0, 120, 255, 0.9)',
    });
  }

  chartDataToReturn.datasets.push({
    label: interval + (props.dosePlan.secondaryTimeSeries ? '' : '(population)'),
    data: props.dosePlan.mainTimeSeries.map((d: any) => ({ x: d.time, y: d.concentration })),
    showLine: true,
    backgroundColor: props.dosePlan.secondaryTimeSeries
      ? 'rgba(108, 2, 189, 0.8)'
      : 'rgba(235, 10, 10, 0.8)',
    borderColor: props.dosePlan.secondaryTimeSeries
      ? 'rgba(128, 2, 169, 0.9)'
      : 'rgba(255, 0, 0, 0.9)',
  });

  if (props.showSecondary && props.dosePlan.secondaryTimeSeries) {
    chartDataToReturn.datasets.push({
      label: interval + '(population)',
      data: props.dosePlan.secondaryTimeSeries.map((d: any) => ({ x: d.time, y: d.concentration })),
      showLine: true,
      hidden: true,
      borderWidth: 1,
      borderDash: [10, 5],
      backgroundColor: 'rgba(235, 20, 20, 0.5)',
      borderColor: 'rgba(255, 0, 0, 0.6)',
    });
  }

  if (props.dosePlan.doseRegime.deliveryMethod === 'CONT') {
    chartDataToReturn.datasets.push({
      label: 'Steady state',
      data: getSteadyStateLevel.value,
      showLine: true,
      hidden: true,
      pointRadius: 0,
      borderWidth: 1,
      borderDash: [10, 5],
      backgroundColor: 'rgba(235, 103, 245, 0.7)',
      borderColor: 'rgba(215, 83, 225, 0.8)',
    });
  } else if (props.dosePlan.doseRegime.deliveryMethod === 'ORAL') {
    chartDataToReturn.datasets.push({
      label: 'Peak',
      data: getPeakLevel.value,
      showLine: true,
      hidden: true,
      pointRadius: 0,
      borderWidth: 1,
      borderDash: [10, 5],
      backgroundColor: 'rgba(245, 128, 2, 0.7)',
      borderColor: 'rgba(255, 100, 0, 0.8)',
    });
  } else {
    chartDataToReturn.datasets.push({
      label: 'Peak',
      data: getPeakLevel.value,
      showLine: true,
      hidden: true,
      pointRadius: 0,
      borderWidth: 1,
      borderDash: [10, 5],
      backgroundColor: 'rgba(245, 128, 2, 0.7)',
      borderColor: 'rgba(255, 100, 0, 0.8)',
    });
    chartDataToReturn.datasets.push({
      label: 'Trough',
      data: getTroughLevel.value,
      showLine: true,
      hidden: true,
      pointRadius: 0,
      borderWidth: 1,
      borderDash: [10, 5],
      backgroundColor: 'rgba(250, 180, 105, 0.7)',
      borderColor: 'rgba(230, 160, 85, 0.8)',
    });
  }

  return chartDataToReturn;
});

function loadChart() {
  const canvas = chartCanvas.value;
  if (!canvas) return;
  const chartData = getChartData.value;
  if (chart.value) {
    chart.value.data = chartData;
    chart.value.update();
  } else {
    chart.value = markRaw(
      new Chart(canvas, {
        type: 'scatter',
        data: chartData,
        options: chartOptions,
        plugins: [dayBoundaryPlugin],
      }),
    );
  }
}

onMounted(() => {
  loadChart();
});
watch(simulation, () => {
  loadChart();
});
watch(
  () => props.dosePlan,
  () => {
    loadChart();
  },
);
watch(dailyAucs, () => {
  chart.value?.update();
});
</script>

<style scoped>
.day-tooltip {
  position: absolute;
  background: rgba(40, 40, 40, 0.88);
  color: #fff;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 10;
}
.day-tooltip-title {
  font-weight: bold;
  margin-bottom: 2px;
}
.tooltip-simulated {
  opacity: 0.7;
  font-style: italic;
}
</style>
